import React from 'react'
import { motion } from 'framer-motion'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import { format } from 'date-fns'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const DCABasicResult = ({ result }) => {
  if (!result) return null

  const data = result
  const formattedDate = format(new Date(data.timestamp), 'PPpp')

  const chartData = {
    labels: data.levels.map((lvl) => `Order ${lvl.order}`),
    datasets: [
      {
        label: 'Price per Order',
        data: data.levels.map((lvl) => lvl.price),
        backgroundColor: '#3b82f6',
        borderRadius: 8,
        barThickness: 24,
      },
      {
        label: 'Cumulative Quantity',
        data: data.levels.map((lvl) => lvl.total_quantity),
        backgroundColor: '#10b981',
        borderRadius: 8,
        barThickness: 24,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: { color: '#fff', font: { size: 14 } },
      },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${ctx.raw}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          color: '#fff'
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: { color: '#fff' },
        grid: { display: false }
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl p-6 shadow-xl bg-slate-800/50 text-white backdrop-blur-lg border border-white/10"
    >
      <h2 className="text-2xl font-bold mb-4">DCA Basic Result</h2>
      <p className="text-sm text-gray-300 mb-6">
        Data reflects the most recent update as of: {formattedDate}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Symbol" value={`${data.symbol}/${data.quote_currency}`} />
        <InfoBlock label="Entry Price" value={`$${data.entry_price}`} />
        <InfoBlock label="Price Drop per Order" value={`${data.price_drop_pct}%`} />
        <InfoBlock label="# of Orders" value={data.num_orders} />
        <InfoBlock label="Investment/Order" value={`$${data.investment_per_order}`} />
      </div>

      <div className="mt-4 mb-8">
        <h3 className="text-lg font-semibold mb-3">Order Breakdown</h3>
        <div className="overflow-x-auto rounded-xl">
          <table className="min-w-full text-sm text-left bg-zinc-800/50 border border-white/10 rounded-xl">
            <thead className="bg-zinc-700/50 text-gray-300">
              <tr>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Quantity</th>
                <th className="px-4 py-2">Total Qty</th>
                <th className="px-4 py-2">Avg Price</th>
                <th className="px-4 py-2">Total Investment</th>
              </tr>
            </thead>
            <tbody>
              {data.levels.map((level) => (
                <tr key={level.order} className="border-t border-zinc-700">
                  <td className="px-4 py-2">{level.order}</td>
                  <td className="px-4 py-2">${level.price.toFixed(2)}</td>
                  <td className="px-4 py-2">{level.quantity}</td>
                  <td className="px-4 py-2">{level.total_quantity}</td>
                  <td className="px-4 py-2">${level.average_price.toFixed(2)}</td>
                  <td className="px-4 py-2">${level.total_investment.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 mb-6">
        <h3 className="text-lg font-semibold mb-2">Investment Visualization</h3>
        <Bar data={chartData} options={chartOptions} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoBlock label="Final Avg Price" value={`$${data.summary.final_average_price.toFixed(2)}`} />
        <InfoBlock label="Total Quantity" value={data.summary.total_quantity} />
        <InfoBlock label="Total Investment" value={`$${data.summary.total_investment.toFixed(2)}`} />
      </div>

      <p className="text-xs text-gray-300 mt-6">
        This is tentative data and may not reflect precise values.
      </p>
    </motion.div>
  )
}

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-400">{label}</p>
    <p className="font-semibold">{value}</p>
  </div>
)

export default DCABasicResult
